import { useQuery, useMutation, useSubscription } from "./urqlHooks";
import { useSelectedCompanyId } from "./tenantCompanyId";

/**
 * matchs[] fields on Transaction in match mutations — align with GET_TRANSACTION / GET_ALL_TRANSACTIONS
 * so Redux editData keeps per-match entries (completed) after generateMatch / unmatch.
 */
const TRANSACTION_MUTATION_MATCH_FIELDS = `            matchId
            docId
            amount
            originalAmount
            currency
            matchSource
            executionId
            ruleId
            origin
            obligationType
            description
            userRelated {
              userId
              firstName
              lastName
              date
              email
            }
            matchedAmount
            exchangeRate
            localValue
            matchCurrency
            accountPlan
            eoaf {
              type
              label
              key
              amount
            }
            entries {
              completed
              amount
              entryList {
                entryId
                entryType
                entryNumber
                entryDate
                period
              }
            }`;

const GET_ALL_TRANSACTIONS = `
  query GET_TRANSACTIONS(
      $pagination: PaginationInput!
      $input: TransactionsInput!
      $type: TransactionType
      $searchFilter: TransactionSearch
    ) {
      getTransactions(
        type: $type
        pagination: $pagination
        input: $input
        searchFilter: $searchFilter
      ) {
        totalDocs
        docs {
          id
          type
          companyId
          date
          stringDate
          amount
          entries {
            completed
            amount
            entryList {
              entryId
              entryType
              entryNumber
              entryDate
              period
            }
          }
          description
          bankDocId
          issuer
          issuerName
          receiver
          receiverName
          comments
          bankId
          passwordId
          accountBalance
          remaining
          accountNumber
          currency
          amountCLP
          isReported
          possibleDuplicate
          possibleDuplicatePeers {
            id
            stringDate
            amount
            description
            accountNumber
            currency
            type
          }
          entryStatus {
            amount
            completed
          }
          fieldKey
          fieldKeyModifiedByHand
          matchs {
            matchId
            docId
            amount
            originalAmount
            currency
            matchSource
            executionId
            ruleId
            origin
            obligationType
            userRelated {
              userId
              firstName
              lastName
              date
              email
            }
            matchedAmount
            exchangeRate
            localValue
            matchCurrency
            eoaf {
              type
              label
              key
              amount
            }
            entries {
              completed
              amount
              entryList {
                entryId
                entryType
                entryNumber
                entryDate
                period
              }
            }
          }
          innerComments {
            date
            comment
            user {
              id
              name
              email
            }
          }
          strongMatchCandidates {
            documentId
          }
          weakMatchCandidates {
            documentId
          }
          matchRecommendationsCount
        }
      }
    }
`;

export const GetTransactions = (pagination, input, type, searchFilter) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_ALL_TRANSACTIONS,
    variables: { pagination, input, type, searchFilter },
    // Cartola: siempre red para ver matches recién hechos sin depender del cache.
    requestPolicy: 'network-only',
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const GET_TRANSACTIONS_ACCOUNTS_BANKS_LIST = `query GET_TRANSACTIONS_ACCOUNTS_BANKS_LIST( $companyId: String! ) { 
  transactionAccountBankList( companyId: $companyId) { 
    accountNumber
    bankId
    currency
  }}`;

export const GetTransactionsAccountBankList = (companyId) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_TRANSACTIONS_ACCOUNTS_BANKS_LIST,
    variables: { companyId },
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};


const CREATE_TRANSACTION = `mutation CREATE_TRANSACTION($input: TransactionsInput!, $companyId: String!) {
  createTransaction(input: $input, companyId: $companyId) {
    id
  }
}`;

export const CreateTransaction = () => {
  const [_exclude, createTransaction] = useMutation(CREATE_TRANSACTION);
  return createTransaction;
};

const CREATE_BULK_TRANSACTIONS = `mutation CREATE_BULK_TRANSACTIONS($input: [TransactionsInput!]!) {
  createBulkTransactions(input: $input) {
    status
    message
  }
}`;

export const CreateBulkTransactions = () => {
  const [_exclude, createBulkTransactions] = useMutation(CREATE_BULK_TRANSACTIONS);
  return createBulkTransactions;
};

const UPDATE_CARTOLA = `mutation REFETCH_BANK_DATA($companyId: String!) {
  refetchBankData(companyId: $companyId)
}`;

export const UpdateCartola = () => {
  const [_result, updateCartola] = useMutation(UPDATE_CARTOLA);
  return updateCartola;
};
const CREATE_EXCEL_TRANSACTIONS = `
mutation GET_ALL_TRANSACTIONS(
  $input: TransactionsInput!, 
  $type: TransactionType,
  $masked: Boolean
  ) {
  getAllTransactions(
    type: $type, 
    input: $input,
    masked: $masked
    )}`;

export const CreateExcelTransactions = () => {
  const [_url, createExcelTransactions] = useMutation(CREATE_EXCEL_TRANSACTIONS);
  return createExcelTransactions;
};

/** Sobre este umbral la exportación va a Ajustes → Descargas (async). */
export const CONCILIACION_EXPORT_ASYNC_THRESHOLD = 100;

/** Conteo liviano (solo totalDocs) para decidir sync vs Descargas. */
const COUNT_TRANSACTIONS_FOR_EXPORT = `
  query COUNT_TRANSACTIONS_FOR_EXPORT(
    $pagination: PaginationInput!
    $input: TransactionsInput!
    $type: TransactionType
  ) {
    getTransactions(
      type: $type
      pagination: $pagination
      input: $input
    ) {
      totalDocs
    }
  }
`;

export { COUNT_TRANSACTIONS_FOR_EXPORT };

const FIND_MATCHS_BY_TRANSACTION_ID = `query FIND_MATCHS_BY_TRANSACTION_ID($transactionId: String!, $dte: Boolean!, $companyId: String!) {
  allTransactionsByDocumentId(documentId: $transactionId, dte: $dte, companyId: $companyId) {
    id
    date
    amount
    type
    description
    bankDocId
    bankId
    passwordId
    remaining
    accountNumber
  }
  allMatchManualByTransactionId(documentId: $transactionId, companyId: $companyId) {
    settleId
    amount
    type
    origin
    currencyType
    userRelated {
      userId
      firstName
      lastName
      date
    }
  }
  allMatchByClay(documentId: $transactionId, companyId: $companyId) {
    settleId
    amount
    type
    origin
    userRelated {
      userId
      firstName
      lastName
      date
    }
  }
  allMatchsByTransactionId(transactionId: $transactionId, dte: $dte, companyId: $companyId) {
    amount
    name
    description
    documentType
    isHidden
    companyId
    currencyType
    id
    agendaId
    documentDate
    isManual
    emissionDate
    documentPeriod
    companyId
    fieldKey
    cashflowArea {
      id
      name
    }
    cashflowAreaId
    meta {
      ... on TaxDocumentMeta {
        documentId
        documentCode
        originalAmount
        netAmount
          taxAmount
          otherTaxes {
             name
             value
            }
        issuedAt
        isPaid
        paymentDate
        isReceived
        issuerId
        issuerName
        receiverId
        receiverName
        taxRate
        remaining
        summary
        taxableAmount
        nonTaxableAmount
        nonHoldableTaxes
        nonRecoverableTaxes
        recoverableTaxes
        lateTaxes
        taxAmount
        issuedAt
        dueDate
        matchs {
          inMatch {
            matchId
            id
            amount
            selected
            currencyType
            matchSource
            executionId
            ruleId
            origin
            eoaf {
              type
              label
              key
              amount
            }
            userRelated {
              userId
              firstName
              lastName
              date
              email
            }
            accountPlan {
              level1 {
                value
                label
              }
              level2 {
                value
                label
              }
              level3 {
                value
                label
              }
            }
            entries {
              completed
            }
          }
          outMatch {
            matchId
            id
            amount
            selected
            matchSource
            executionId
            ruleId
            origin
            userRelated {
              userId
              firstName
              lastName
              date
              email
            }
            accountPlan {
              level1 {
                value
                label
              }
              level2 {
                value
                label
              }
              level3 {
                value
                label
              }
            }
            entries {
              completed
            }
          }
        }
      }
    }
    meta {
      ... on ObligationMeta {
        family
        familyLabel
        type
        typeLabel
        description
        date
        documentDate
        number
        documentId
        amount
        currency
        isPaid
        paymentDate
        remaining
        originalAmount
        taxRate
        issuerId
        isReceived
        issuerName
        receiverId
        receiverName
        login
        transactionNumber
        ticket
        empresa {
          name
        }
        creditDocumentId
        parentCreditId
        exchange {
          originalAmount
          originalCurrency
          localAmount
          localCurrency
          exchangeRate
          exchangeDate
        }
        matchs {
          inMatch {
            matchId
            id
            amount
            selected
            currencyType
            matchSource
            executionId
            ruleId
            origin
            userRelated {
              userId
              firstName
              lastName
              date
              email
            }
            accountPlan {
              level1 {
                value
                label
              }
              level2 {
                value
                label
              }
              level3 {
                value
                label
              }
            }
            entries {
              completed
            }
          }
          outMatch {
            matchId
            id
            amount
            selected
            matchSource
            executionId
            ruleId
            origin
            userRelated {
              userId
              firstName
              lastName
              date
              email
            }
            accountPlan {
              level1 {
                value
                label
              }
              level2 {
                value
                label
              }
              level3 {
                value
                label
              }
            }
            entries {
              completed
            }
          }
        }
      }
    }
    meta {
      ... on CreditsMeta {
        installments
        interest
        stock
        totalPayments
        paymentNumber
        isPaid
        identificator
      }
    }
  }
}`;

export const FindMatchsByTransactionId = ({ transactionId, dte, companyId: companyIdProp }) => {
  const selectedCompanyId = useSelectedCompanyId();
  const companyId = companyIdProp || selectedCompanyId;
  const [result, reexecuteQuery] = useQuery({
    query: FIND_MATCHS_BY_TRANSACTION_ID,
    variables: { transactionId: transactionId || '', dte: dte ?? false, companyId: companyId || '' },
    pause: !transactionId || !companyId,
    requestPolicy: 'network-only',
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const DELETE_MATCH = `mutation DELETE_MATCH($match: deleteMatchInput!, $companyId: String!) {
  deleteMatch(match: $match, companyId: $companyId) {
    id
  }
}`;

export const DeleteMatch = () => {
  const [_result, deleteMatch] = useMutation(DELETE_MATCH);
  return deleteMatch;
};

const GENERATE_MATCH = `mutation GENERATE_MATCH($input: GenerateMatchInput!, $companyId: String!) {
  generateMatch(input: $input, companyId: $companyId) {
      data {
        ... on Document {
          meta {
            ... on TaxDocumentMeta {
              remaining
              isPaid
              matchs {
                inMatch {
                  matchId
                  id
                  amount
                  selected
                  matchSource
                  origin
                  userRelated {
                    userId
                    firstName
                    lastName
                    date
                    email
                  }
                }
                outMatch {
                  matchId
                  id
                  amount
                  selected
                  matchSource
                  origin
                  userRelated {
                    userId
                    firstName
                    lastName
                    date
                    email
                  }
                }
              }
            }
          }  
          meta {
            ... on ObligationMeta {
              remaining
              isPaid
              creditDocumentId
              parentCreditId
              matchs {
                inMatch {
                  matchId
                  id
                  amount
                  selected
                  matchSource
                  origin
                  userRelated {
                    userId
                    firstName
                    lastName
                    date
                    email
                  }
                }
                outMatch {
                  matchId
                  id
                  amount
                  selected
                  matchSource
                  origin
                  userRelated {
                    userId
                    firstName
                    lastName
                    date
                    email
                  }
                }
              }
            }
          }
        }
        ... on Transaction {
          remaining
          matchs {
${TRANSACTION_MUTATION_MATCH_FIELDS}
          }
        }
      }
    }   
  }
`;
export const GenerateMatch = () => {
  const [result, executeMutation] = useMutation(GENERATE_MATCH);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const RECONCILE_INTER_ACCOUNT_TRANSFER = `mutation RECONCILE_INTER_ACCOUNT_TRANSFER($input: ReconcileInterAccountTransferInput!) {
  reconcileInterAccountTransfer(input: $input) {
    success
    traspasoId
    rendicionId
    cargoTxId
    abonoTxId
    amount
  }
}`;

export const ReconcileInterAccountTransfer = () => {
  const [result, executeMutation] = useMutation(RECONCILE_INTER_ACCOUNT_TRANSFER);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const GENERATE_MASSIVE_MATCHES = `mutation GENERATE_MASSIVE_MATCHES($inputs: [GenerateMatchInput!]!, $companyId: String!) {
  generateMassiveMatches(inputs: $inputs, companyId: $companyId) {
    data {
      ... on Document {
        meta {
          ... on TaxDocumentMeta {
            remaining
            isPaid
            matchs {
              inMatch {
                matchId
                id
                amount
                selected
                matchSource
                origin
                userRelated {
                  userId
                  firstName
                  lastName
                  date
                  email
                }
              }
              outMatch {
                matchId
                id
                amount
                selected
                matchSource
                origin
                userRelated {
                  userId
                  firstName
                  lastName
                  date
                  email
                }
              }
            }
          }
          ... on ObligationMeta {
            remaining
            isPaid
            creditDocumentId
            parentCreditId
            matchs {
              inMatch {
                matchId
                id
                amount
                selected
                matchSource
                origin
                userRelated {
                  userId
                  firstName
                  lastName
                  date
                  email
                }
              }
              outMatch {
                matchId
                id
                amount
                selected
                matchSource
                origin
                userRelated {
                  userId
                  firstName
                  lastName
                  date
                  email
                }
              }
            }
          }
        }  
      }
      ... on Transaction {
        remaining
        matchs {
${TRANSACTION_MUTATION_MATCH_FIELDS}
        }
      }
    }
  }
}`;

export const GenerateMassiveMatches = () => {
  const [result, executeMutation] = useMutation(GENERATE_MASSIVE_MATCHES);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};



const UN_GENERATE_MATCH = `mutation UN_GENERATE_MATCH($input: GenerateMatchInput!, $companyId: String!) {
  generateUnmatch(input: $input, companyId: $companyId) {
      data {
        ... on Document {
          meta {
            ... on TaxDocumentMeta {
              remaining
              isPaid
              matchs {
                inMatch {
                  matchId
                  id
                  amount
                  selected
                  matchSource
                  origin
                  userRelated {
                    userId
                    firstName
                    lastName
                    date
                    email
                  }
                }
                outMatch {
                  matchId
                  id
                  amount
                  selected
                  matchSource
                  origin
                  userRelated {
                    userId
                    firstName
                    lastName
                    date
                    email
                  }
                }
              }
            }
          }  
          meta {
            ... on ObligationMeta {
              remaining
              isPaid
              creditDocumentId
              parentCreditId
              matchs {
                inMatch {
                  matchId
                  id
                  amount
                  selected
                  matchSource
                  origin
                  userRelated {
                    userId
                    firstName
                    lastName
                    date
                    email
                  }
                }
                outMatch {
                  matchId
                  id
                  amount
                  selected
                  matchSource
                  origin
                  userRelated {
                    userId
                    firstName
                    lastName
                    date
                    email
                  }
                }
              }
            }
          }
        }
        ... on Transaction {
          remaining
          matchs {
${TRANSACTION_MUTATION_MATCH_FIELDS}
          }
        }
      }
    }   
  }
`;
export const UnGenerateMatch = () => {
  const [result, executeMutation] = useMutation(UN_GENERATE_MATCH);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const GENERATE_MULTIPLE_MATCH = `mutation GENERATE_MULTIPLE_MATCH($input: GenerateMultipleMatchInput!, $companyId: String!) {
  generateMultipleMatch(input: $input, companyId: $companyId) {
    data {
      ... on Document {
        meta {
          ... on TaxDocumentMeta {
            remaining
            isPaid
            matchs {
              inMatch {
                matchId
                id
                amount
                selected
                matchSource
                origin
                userRelated {
                  userId
                  firstName
                  lastName
                  date
                  email
                }
              }
              outMatch {
                matchId
                id
                amount
                selected
                matchSource
                origin
                userRelated {
                  userId
                  firstName
                  lastName
                  date
                  email
                }
              }
            }
          }
        }  
        meta {
          ... on ObligationMeta {
            remaining
            isPaid
            creditDocumentId
            parentCreditId
            matchs {
              inMatch {
                matchId
                id
                amount
                selected
                matchSource
                origin
                userRelated {
                  userId
                  firstName
                  lastName
                  date
                  email
                }
              }
              outMatch {
                matchId
                id
                amount
                selected
                matchSource
                origin
                userRelated {
                  userId
                  firstName
                  lastName
                  date
                  email
                }
              }
            }
          }
        }
      }
      ... on Transaction {
        remaining
        matchs {
${TRANSACTION_MUTATION_MATCH_FIELDS}
        }
      }
    }
  }
  }
`;

export const GenerateMultipleMatch = () => {
  const [result, executeMutation] = useMutation(GENERATE_MULTIPLE_MATCH);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const GENERATE_MULTIPLE_UNMATCH = `mutation GENERATE_MULTIPLE_UNMATCH($input: [GenerateMatchInput!]!, $companyId: String!) {
  generateMultipleUnMatch(input: $input, companyId: $companyId) {
    data {
      ... on Document {
        meta {
          ... on TaxDocumentMeta {
            remaining
            isPaid
            matchs {
              inMatch {
                matchId
                id
                amount
                selected
                matchSource
                origin
                userRelated {
                  userId
                  firstName
                  lastName
                  date
                  email
                }
              }
              outMatch {
                matchId
                id
                amount
                selected
                matchSource
                origin
                userRelated {
                  userId
                  firstName
                  lastName
                  date
                  email
                }
              }
            }
          }
        }  
        meta {
          ... on ObligationMeta {
            remaining
            isPaid
            creditDocumentId
            parentCreditId
            matchs {
              inMatch {
                matchId
                id
                amount
                selected
                matchSource
                origin
                userRelated {
                  userId
                  firstName
                  lastName
                  date
                  email
                }
              }
              outMatch {
                matchId
                id
                amount
                selected
                matchSource
                origin
                userRelated {
                  userId
                  firstName
                  lastName
                  date
                  email
                }
              }
            }
          }
        }
      }
      ... on Transaction {
        remaining
        matchs {
${TRANSACTION_MUTATION_MATCH_FIELDS}
        }
      }
    }
  }
  }
`;

export const GenerateMultipleUnMatch = () => {
  const [result, executeMutation] = useMutation(GENERATE_MULTIPLE_UNMATCH);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

/* -------------------------------------------------------------------------
 * Desconciliación masiva
 *
 * A diferencia de GenerateMultipleUnMatch (síncrono, y que aborta al primer
 * error), acá se lanza un job: la mutation responde el jobId al instante y el
 * avance llega por subscription — con polling de respaldo, porque el PubSub del
 * server es in-memory por pod y el evento no cruza pods.
 * ------------------------------------------------------------------------- */

const BULK_UNMATCH_JOB_FIELDS = `
  id
  companyId
  type
  status
  percentage
  message
  cursorIndex
  deleteMatchCreatedObligations
  failuresTruncated
  cancelRequested
  createdAt
  startedAt
  finishedAt
  totals {
    targets
    targetsProcessed
    matches
    unmatched
    skipped
    failed
    obligationsDeleted
    entriesDeleted
  }
  failures {
    targetId
    counterpartId
    matchId
    code
    message
  }
`;

const PREVIEW_BULK_UNMATCH = `mutation PREVIEW_BULK_UNMATCH($input: BulkUnmatchInput!) {
  previewBulkUnmatch(input: $input) {
    targets
    targetsWithMatches
    totalMatches
    entriesToDelete
    obligationsToDelete
    blockedByPeriod
    blockers
    warnings
    canProceed
  }
}`;

/** Dry-run: qué se destruiría. Se llama al abrir el modal, antes de confirmar. */
export const PreviewBulkUnmatch = () => {
  const [result, executeMutation] = useMutation(PREVIEW_BULK_UNMATCH);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const BULK_UNMATCH = `mutation BULK_UNMATCH($input: BulkUnmatchInput!) {
  bulkUnmatch(input: $input) {
${BULK_UNMATCH_JOB_FIELDS}
  }
}`;

export const BulkUnmatch = () => {
  const [result, executeMutation] = useMutation(BULK_UNMATCH);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const GET_BULK_UNMATCH_JOB = `query GET_BULK_UNMATCH_JOB($jobId: String!) {
  bulkUnmatchJob(jobId: $jobId) {
${BULK_UNMATCH_JOB_FIELDS}
  }
}`;

/**
 * Polling de respaldo del job. `pause` lo apaga cuando no hay job en vuelo.
 * No es redundante con la subscription: el WS solo llega si el cliente está
 * conectado al mismo pod que corre el job.
 */
export const GetBulkUnmatchJob = (jobId, pause = false) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_BULK_UNMATCH_JOB,
    variables: { jobId },
    pause: pause || !jobId,
    requestPolicy: "network-only",
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const GET_BULK_UNMATCH_JOBS = `query GET_BULK_UNMATCH_JOBS($companyId: String!, $limit: Float) {
  bulkUnmatchJobs(companyId: $companyId, limit: $limit) {
${BULK_UNMATCH_JOB_FIELDS}
  }
}`;

/** Historial; sirve para retomar el toast de un job en curso tras recargar. */
export const GetBulkUnmatchJobs = (companyId, limit = 5, pause = false) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_BULK_UNMATCH_JOBS,
    variables: { companyId, limit },
    pause: pause || !companyId,
    requestPolicy: "network-only",
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const CANCEL_BULK_UNMATCH_JOB = `mutation CANCEL_BULK_UNMATCH_JOB($jobId: String!) {
  cancelBulkUnmatchJob(jobId: $jobId) {
    id
    status
    cancelRequested
  }
}`;

export const CancelBulkUnmatchJob = () => {
  const [result, executeMutation] = useMutation(CANCEL_BULK_UNMATCH_JOB);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const RETRY_BULK_UNMATCH_JOB = `mutation RETRY_BULK_UNMATCH_JOB($jobId: String!) {
  retryBulkUnmatchJob(jobId: $jobId) {
${BULK_UNMATCH_JOB_FIELDS}
  }
}`;

export const RetryBulkUnmatchJob = () => {
  const [result, executeMutation] = useMutation(RETRY_BULK_UNMATCH_JOB);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const BULK_UNMATCH_PROGRESS_SUB = `subscription BULK_UNMATCH_PROGRESS($companyId: String!) {
  bulkUnmatchProgress(companyId: $companyId) {
    companyId
    jobId
    status
    percentage
    message
    totals {
      targets
      targetsProcessed
      matches
      unmatched
      skipped
      failed
      obligationsDeleted
      entriesDeleted
    }
  }
}`;

export const BulkUnmatchProgressSubscription = (companyId, handler, pause = false) => {
  const [result] = useSubscription(
    {
      query: BULK_UNMATCH_PROGRESS_SUB,
      variables: { companyId: companyId || "" },
      pause: pause || !companyId,
    },
    handler,
  );
  return result;
};

const CREATE_INNER_COMMENT = `mutation CREATE_INNER_COMMENT($input: InnerComment!, $companyId: String!) {
  createInnerComment(input: $input, companyId: $companyId){
    id
  }
}`;

export const CreateInnerComment = () => {
  const [result, executeMutation] = useMutation(CREATE_INNER_COMMENT);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const CREATE_BULK_INNER_COMMENTS = `mutation CREATE_BULK_INNER_COMMENTS($input: BulkInnerCommentInput!, $companyId: String!) {
  createBulkInnerComments(input: $input, companyId: $companyId){
    id
  }
}`;

export const CreateBulkInnerComments = () => {
  const [result, executeMutation] = useMutation(CREATE_BULK_INNER_COMMENTS);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const UPDATE_TRANSACTIONS = `mutation UPDATE_TRANSACTIONS($companyId: String!, $input: DocumentsToModify!) {
  updateTransactions(companyId: $companyId, input: $input)
}`;

export const UpdateTransactions = () => {
  const [result, executeMutation] = useMutation(UPDATE_TRANSACTIONS);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const RECORD_SYSTEM_RECOMMENDATION_DECISION = `
  mutation RecordSystemRecommendationDecision($input: RecordSystemRecommendationDecisionInput!) {
    recordSystemRecommendationDecision(input: $input)
  }
`;

export const RecordSystemRecommendationDecision = () => {
  const [result, executeMutation] = useMutation(RECORD_SYSTEM_RECOMMENDATION_DECISION);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const GET_TRANSACTION = `query GET_TRANSACTION($id: String!, $companyId: String!) {
  getTransaction(id: $id, companyId: $companyId) {
    id
    _id
    type
    companyId
    date
    stringDate
    amount
    description
    bankDocId
    issuer
    issuerName
    receiver
    receiverName
    comments
    bankId
    passwordId
    accountBalance
    remaining
    accountNumber
    currency
    amountCLP
    isReported
    possibleDuplicate
    possibleDuplicatePeers {
      id
      stringDate
      amount
      description
      accountNumber
      currency
      type
    }
    entries {
      completed
      amount
      entryList {
        entryId
        entryType
        entryNumber
        entryDate
        period
      }
    }
    matchs {
      matchId
      docId
      amount
      originalAmount
      currency
      matchSource
      origin
      obligationType
      description
      userRelated {
        userId
        firstName
        lastName
        date
        email
      }
      matchedAmount
      exchangeRate
      localValue
      matchCurrency
      eoaf {
        type
        label
        key
        amount
      }
      entries {
        completed
      }
    }
    innerComments {
      date
      comment
      user {
        id
        name
        email
      }
    }
    attachedFiles {
      fileName
      originalFileName
      uploadedAt
      uploadedBy
    }
    strongMatchCandidates {
      documentId
    }
    weakMatchCandidates {
      documentId
    }
    aiRecommendations {
      documentId
      source
      score
      recommendationId
      obligationType
      obligationTypeLabel
      suggestedDescription
      suggestedAmount
      recommendationType
      suggestedDocId
    }
  }
}`;

export const GetTransaction = (id, companyIdOverride) => {
  const selectedCompanyId = useSelectedCompanyId();
  const companyId = companyIdOverride || selectedCompanyId;
  const [result, reexecuteQuery] = useQuery({
    query: GET_TRANSACTION,
    variables: { id: id || '', companyId: companyId || '' },
    pause: !id || !companyId,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const RECORD_MOVEMENT_SHARE_LINK = `mutation RecordMovementShareLink(
  $companyId: String!,
  $transactionId: String!,
  $url: String
) {
  recordMovementShareLink(
    companyId: $companyId,
    transactionId: $transactionId,
    url: $url
  )
}`;

export const RecordMovementShareLink = () => {
  const [result, executeMutation] = useMutation(RECORD_MOVEMENT_SHARE_LINK);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const REMOVE_NOTE_FROM_TRANSACTION = `mutation removeNoteFromTransaction(
  $companyId: String!,
  $transactionId: String!,
  $noteIndexToRemove: Float!,
){
  removeNoteFromTransaction(
    companyId: $companyId,
    transactionId: $transactionId,
    noteIndexToRemove: $noteIndexToRemove
  ){
    ok
    nModified
  }
}`;

export const RemoveNoteFromTransaction = () => {
  const [result, executeMutation] = useMutation(REMOVE_NOTE_FROM_TRANSACTION);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const CLASSIFY_EOAFS = `mutation CLASSIFY_EOAFS($transactions:[String!]!, $fieldKey: String!, $companyId: String!) {
  setEOAFCategory(transactions:$transactions, fieldKey: $fieldKey, companyId: $companyId)  
}`;

export const ClassifyEOAFS = () => {
  const [result, classifyEOAFS] = useMutation(CLASSIFY_EOAFS);
  const { data, fetching, error } = result;
  return { data, fetching, error, classifyEOAFS };
};

const FIND_TRANSACTION_BY_ID = `query FIND_TRANSACTION_BY_ID($ids: [String!]!, $companyId: String!) {
  findTransactionsById(ids: $ids, companyId: $companyId) {
    id
    type
    companyId
    date
    stringDate
    amount
    description
    bankDocId
    issuer
    issuerName
    receiver
    receiverName
    comments
    bankId
    passwordId
    accountBalance
    remaining
    accountNumber
    currency
    amountCLP
    period
    flexiblePeriod
    fieldKey
    matchs {
      matchId
      docId
      amount
      originalAmount
      currency
      matchSource
      origin
      obligationType
      description
      eoaf {
        type
        label
        key
        amount
      }
      userRelated {
        userId
        firstName
        lastName
        date
        email
      }
      matchedAmount
      exchangeRate
      localValue
      matchCurrency
      entries {
        completed
      }
    }
    innerComments {
      date
      comment
      user {
        id
        name
        email
      }
    }
    strongMatchCandidates {
      documentId
    }
    weakMatchCandidates {
      documentId
    }
  }
}`;

export const FindTransactionById = (variables) => {
  const [result, reexecuteQuery] = useQuery({
    query: FIND_TRANSACTION_BY_ID,
    variables,
    pause: !variables?.ids || variables.ids.length === 0,
  });

  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const GET_POSIBLE_MATCHS = `query GetPosibleMatchedTransactions($companyId: String!) {
    getPosibleMatchedTransactions(companyId: $companyId) {
        id
        type
        companyId
        date
        stringDate
        amount
        accountBalance
        description
        bankDocId
        issuer
        receiver
        receiverName
        issuerName
        comments
        bankId
        accountNumber
        remaining
        currency
        amountCLP
        key
        isHidden
        origin
        floidCaseId
        fieldKey
        period
        flexiblePeriod
        fieldKeyModifiedByHand
        strongMatchCandidates {
            documentId
        }
        weakMatchCandidates {
            documentId
        }
    }
}`;

export const GetPosibleMatch = (companyId) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_POSIBLE_MATCHS,
    variables: { companyId },
  });

  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};
